import { useState, useCallback } from 'react';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: any) => string | null;
}

export interface ValidationRules {
  [field: string]: ValidationRule;
}

export interface ValidationErrors {
  [field: string]: string;
}

export interface UseFormValidationResult {
  errors: ValidationErrors;
  validate: (field: string, value: any) => boolean;
  validateAll: (values: any) => boolean;
  clearError: (field: string) => void;
  clearAllErrors: () => void;
}

export const useFormValidation = (rules: ValidationRules): UseFormValidationResult => {
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validate = useCallback((field: string, value: any): boolean => {
    const rule = rules[field];
    if (!rule) return true;

    let errorMessage: string | null = null;

    if (rule.required && (!value || (typeof value === 'string' && !value.trim()))) {
      errorMessage = `${field} is required`;
    } else if (rule.minLength && typeof value === 'string' && value.length < rule.minLength) {
      errorMessage = `${field} must be at least ${rule.minLength} characters`;
    } else if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
      errorMessage = `${field} must be no more than ${rule.maxLength} characters`;
    } else if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
      errorMessage = `${field} format is invalid`;
    } else if (rule.custom) {
      errorMessage = rule.custom(value);
    }

    if (errorMessage) {
      setErrors(prev => ({ ...prev, [field]: errorMessage! }));
      return false;
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      return true;
    }
  }, [rules]);

  const validateAll = useCallback((values: any): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    Object.keys(rules).forEach(field => {
      const rule = rules[field];
      const value = values[field];
      let errorMessage: string | null = null;

      if (rule.required && (!value || (typeof value === 'string' && !value.trim()))) {
        errorMessage = `${field} is required`;
      } else if (rule.minLength && typeof value === 'string' && value.length < rule.minLength) {
        errorMessage = `${field} must be at least ${rule.minLength} characters`;
      } else if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
        errorMessage = `${field} must be no more than ${rule.maxLength} characters`;
      } else if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
        errorMessage = `${field} format is invalid`;
      } else if (rule.custom) {
        errorMessage = rule.custom(value);
      }

      if (errorMessage) {
        newErrors[field] = errorMessage;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [rules]);

  const clearError = useCallback((field: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    errors,
    validate,
    validateAll,
    clearError,
    clearAllErrors
  };
};
